import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('tributeDonations');
export interface TributeDonationsRec {
  id: string;
  userId: string;
  tributeType?: string;
  tributeDetails?: string;
  details?: any;
  specialInstructions?: string;
  notes?: string;
  estimatedCost?: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  organizationName?: string;
  isLocked?: boolean;
}

class TributeDonationsService {
  async getAllTributeDonations(): Promise<TributeDonationsRec[]> {
    const response = await http.get('/api/tribute-donations');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch tribute donations information');
    }

    return response.data.data || [];
  }

  async getTributeDonationsById(id: string): Promise<TributeDonationsRec | null> {
    try {
      const response = await http.get(`/api/tribute-donations/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch tribute donations information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching tribute donations by ID:', error);
      return null;
    }
  }

  async createTributeDonations(tributeDonationsData: Partial<TributeDonationsRec>): Promise<TributeDonationsRec> {
    const response = await http.post('/api/tribute-donations', tributeDonationsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save tribute donations information');
    }

    return response.data.data;
  }

  async updateTributeDonations(id: string, tributeDonationsData: Partial<TributeDonationsRec>): Promise<TributeDonationsRec> {
    const response = await http.patch(`/api/tribute-donations/${id}`, tributeDonationsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update tribute donations information');
    }

    return response.data.data;
  }

  async deleteTributeDonations(id: string): Promise<void> {
    const response = await http.delete(`/api/tribute-donations/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete tribute donations information');
    }
  }

  async lockTributeDonations(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for tribute donations ID:', id);
    const response = await http.post(`/api/tribute-donations/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock tribute donations information');
    }
  }

  async unlockTributeDonations(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for tribute donations ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/tribute-donations/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock tribute donations information');
    }
  }

  async getTributeDonationsAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/tribute-donations/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch tribute donations access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching tribute donations access grants:', error);
      return [];
    }
  }

  async deleteTributeDonationsAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/tribute-donations/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const tributeDonationsService = new TributeDonationsService();